/**
 * Form — React Hook Form FormProvider wrapper.
 *
 * Accepts a zodResolver schema and exposes the RHF context to children.
 * Rule 8 critical: form-level onError is surfaced via sonner toast — no
 * silent failures on submit.
 *
 * See: CLAUDE.md Rule 8 — never silently swallow a submit error.
 * See: .plan/features/2026-05-11-framework-adoption.md §3
 */

import React from "react";
import {
  FormProvider,
  type UseFormReturn,
  type FieldValues,
  type SubmitHandler,
  type SubmitErrorHandler,
} from "react-hook-form";
import { toast } from "sonner";

export interface FormProps<TFieldValues extends FieldValues = FieldValues> {
  /** The form object returned by `useForm()`. */
  form: UseFormReturn<TFieldValues>;
  /** Called with validated data when the form submits successfully. */
  onSubmit: SubmitHandler<TFieldValues>;
  /**
   * Called with validation errors when submit fails.
   * Default: shows a sonner toast with "Check the fields above." (Rule 8).
   * Override to customise error handling — but you MUST surface the failure.
   */
  onError?: SubmitErrorHandler<TFieldValues>;
  children: React.ReactNode;
  className?: string;
  id?: string;
  /**
   * Optional extra data-* attributes forwarded verbatim to the <form> element.
   * Used e.g. for data-turnstile-state on the signup form (e2e selectors).
   */
  dataAttrs?: Record<string, string | undefined>;
}

/**
 * Form wraps your RHF `useForm()` result in a `<FormProvider>` and a
 * `<form>` element with built-in Rule 8 error surfacing.
 *
 * @example
 * ```tsx
 * const form = useForm({ resolver: zodResolver(mySchema) });
 * <Form form={form} onSubmit={handleSubmit}>
 *   <TextField name="title" label="Title" />
 *   <button type="submit">Save</button>
 * </Form>
 * ```
 */
export function Form<TFieldValues extends FieldValues = FieldValues>({
  form,
  onSubmit,
  onError,
  children,
  className,
  id,
  dataAttrs,
}: FormProps<TFieldValues>): React.JSX.Element {
  const defaultOnError: SubmitErrorHandler<TFieldValues> = (errors) => {
    // Rule 8: never swallow a validation error silently.
    // Log for debugging; show a toast so the user sees that something failed.
    console.error("[Form] Validation errors:", errors);
    toast.error("Check the fields above.");
  };

  return (
    <FormProvider {...form}>
      <form
        id={id}
        className={className}
        {...dataAttrs}
        onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
          form
            .handleSubmit(
              onSubmit,
              onError ?? defaultOnError,
            )(e)
            .catch((err: unknown) => {
              // Rule 8: catch any unexpected error from the submit handler and
              // surface it — bare catch {} is forbidden.
              console.error("[Form] Unexpected submit error:", err);
              toast.error("Something went wrong. Please try again.");
            });
        }}
        noValidate
      >
        {children}
      </form>
    </FormProvider>
  );
}
